import { getDatabase, ref, get, update, push, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { recordActivity } from './activityApi';
import type { ActivityAction } from '../types/activity';

/**
 * Tombstone written to `deletedFiles/{fileKey}` when a file is soft-deleted.
 *
 * Schema is shared with iOS and Cloud Functions — do not change field names
 * or formats without coordinating all projects. A future cleanup job purges
 * the Storage blobs after a grace period using these tombstones.
 */
export interface DeletedFileTombstone {
    /** Exact DB path the node was removed from, no leading slash ('' if the file had no DB node) */
    originalPath: string;
    /** Entity type, e.g. 'projectPhoto', 'buildingDocument', 'brandingAsset' */
    entityType: string;
    /** Storage object path of the blob ('' only if genuinely unknown) */
    storagePath: string;
    /** The original DB node, verbatim */
    metadata: object;
    /** Firebase UID of the deleting user — never displayName/email */
    deletedBy: string;
    /** formatDateForStorage(new Date()) — "YYYY-MM-DD hh:mm:ss" Europe/Stockholm */
    deletedAt: string;
}

export interface SoftDeleteFileOptions {
    /** DB path of the file metadata node to delete (no leading slash) */
    originalPath: string;
    /** Tombstone entity type, e.g. 'projectPhoto' */
    entityType: string;
    /**
     * Resolves the Storage object path from the node's metadata. Defaults to
     * the node's own `storagePath` field. Provide for entities that store
     * `storageFilename` instead (documents).
     */
    resolveStoragePath?: (metadata: Record<string, unknown>) => string;
    /**
     * Extra multi-path updates (paths relative to the DB root) included in the
     * same atomic write, e.g. stamping the parent's updatedBy/updatedAt.
     */
    extraUpdates?: Record<string, unknown>;
}

/** The Användaraktivitet action of a soft-deleted file, by its tombstone entity type. */
export const fileDeletionActivity = (entityType: string): ActivityAction => {
    if (/blueprint/i.test(entityType)) return 'drawing.deleted';
    if (/photo/i.test(entityType)) return 'photo.deleted';
    return 'document.deleted';
};

/**
 * Soft-delete a file: atomically move its DB metadata node to a tombstone at
 * `deletedFiles/{fileKey}`.
 *
 * The Storage blob is NEVER deleted from the client — Storage rules deny
 * deletes. A future cleanup job purges blobs after a grace period using the
 * tombstones.
 *
 * The node removal, tombstone write, and any `extraUpdates` happen in ONE
 * atomic multi-path `update()` at the DB root.
 */
export const softDeleteFile = async (options: SoftDeleteFileOptions): Promise<Record<string, unknown>> => {
    const { entityType, resolveStoragePath, extraUpdates } = options;
    const db = getDatabase();

    // Tombstones store paths without leading/trailing slashes
    const originalPath = options.originalPath.replace(/^\/+|\/+$/g, '');
    const fileKey = originalPath.split('/').pop();
    if (!fileKey) {
        throw new Error(`Ogiltig sökväg för borttagning: ${options.originalPath}`);
    }

    // Read the node so the tombstone preserves the metadata verbatim
    const snapshot = await get(ref(db, originalPath));
    if (!snapshot.exists()) {
        throw new Error(`Filen hittades inte i databasen (${originalPath})`);
    }
    const metadata = snapshot.val() as Record<string, unknown>;

    const storagePath = resolveStoragePath
        ? resolveStoragePath(metadata)
        : (typeof metadata.storagePath === 'string' ? metadata.storagePath : '');

    const tombstone: DeletedFileTombstone = {
        originalPath,
        entityType,
        storagePath,
        metadata,
        deletedBy: getAuth().currentUser?.uid || '',
        deletedAt: formatDateForStorage(new Date()),
    };

    // ONE atomic multi-path update: remove the node, write the tombstone,
    // and apply any caller-supplied extra updates (e.g. parent stamping)
    const updates: Record<string, unknown> = {
        [originalPath]: null,
        [`deletedFiles/${fileKey}`]: tombstone,
        ...(extraUpdates || {}),
    };

    await update(ref(db), updates);
    // The one log point for file deletions; no targetKey, so a cascade over many files collapses into one entry.
    recordActivity(fileDeletionActivity(entityType), { buildingKey: /^buildings\/([^/]+)\//.exec(originalPath)?.[1] });
    return metadata;
};

/** Tombstone entity type of a drawing's PDF source object (`<raster storagePath>_source`). */
export const BLUEPRINT_SOURCE_ENTITY_TYPE = 'blueprintSource';

/**
 * Soft-delete a drawing (level, roof or parking blueprint): the node moves to
 * its tombstone atomically as for any file (`source` travels inside
 * `metadata`), then the PDF source object gets a storage-only tombstone of
 * its own so the cleanup job finds both blobs. The second write is best
 * effort after the atomic one — an unmigrated drawing has no source and gets
 * one tombstone, as before.
 */
export const softDeleteDrawing = async (options: SoftDeleteFileOptions): Promise<void> => {
    const metadata = await softDeleteFile(options);
    const source = metadata.source;
    if (!source || typeof source !== 'object') return;
    const sourcePath = (source as { storagePath?: unknown }).storagePath;
    if (typeof sourcePath !== 'string' || sourcePath.length === 0) return;
    await tombstoneStorageOnlyFile(BLUEPRINT_SOURCE_ENTITY_TYPE, sourcePath, source);
};

/**
 * Write a tombstone for a Storage blob that has no per-file DB metadata node
 * (e.g. branding assets stored at fixed Storage paths).
 *
 * Generates the tombstone key with `push().key` and does NOT touch any other
 * DB path — the caller is responsible for clearing its own references.
 */
export const tombstoneStorageOnlyFile = async (
    entityType: string,
    storagePath: string,
    metadata?: object
): Promise<void> => {
    const db = getDatabase();

    const fileKey = push(ref(db, 'deletedFiles')).key;
    if (!fileKey) {
        throw new Error('Kunde inte skapa nyckel för borttagningspost');
    }

    const tombstone: DeletedFileTombstone = {
        originalPath: '',
        entityType,
        storagePath,
        metadata: metadata ?? { storagePath },
        deletedBy: getAuth().currentUser?.uid || '',
        deletedAt: formatDateForStorage(new Date()),
    };

    await set(ref(db, `deletedFiles/${fileKey}`), tombstone);
};

/**
 * Best-effort tombstone of every attachment present in `attachments` but
 * absent from `keep` (compared by DB key). For attachment maps embedded in a
 * parent record (template attachments): the blobs lose their DB reference when
 * the parent is saved/deleted or an unsaved upload is abandoned, so each one
 * gets a storage-only tombstone.
 *
 * Failures are swallowed per file — a missed tombstone must never block the
 * user action that triggered it.
 */
export const tombstoneAttachmentDiff = async (
    entityType: string,
    attachments: Record<string, { storagePath?: string }> | undefined,
    keep: Record<string, unknown> | undefined
): Promise<void> => {
    if (!attachments) return;

    const keptKeys = new Set(Object.keys(keep ?? {}));

    await Promise.all(
        Object.entries(attachments)
            .filter(([dbKey]) => !keptKeys.has(dbKey))
            .map(async ([, metadata]) => {
                try {
                    await tombstoneStorageOnlyFile(entityType, metadata.storagePath || '', metadata);
                } catch {
                    // Best effort
                }
            })
    );
};
